import { isPostHogInitialised } from './posthog';
import { enqueueAnalyticsEvent, CaptureProperties } from './queue';
import { currentScreenProperties } from './taxonomy';

/**
 * The single event-capture wrapper every product-analytics chokepoint
 * (pageviews, mutation outcomes, crashes, connectivity summaries) routes
 * through. Keeping the "PostHog not initialised → drop" rule in one place
 * means every call site stays a silent no-op in local dev, CI, and any
 * preview/staging build without a PostHog key — see posthog.ts.
 *
 * Plan 010: events are enqueued into the persistent, offline-aware queue
 * (queue.ts) rather than handed straight to `posthog.capture` — a maquinista
 * operator working offline must not lose events from a session whose tab
 * closes or reloads before connectivity returns. See queue.ts for the flush
 * triggers and failure policy. Callers should not depend on capture() being
 * synchronous beyond "never throws" and "never blocks the caller".
 *
 * BIL-5331: every event carries the taxonomy `module` and `route_name` of
 * the screen it was captured on (docs/product-analytics-contract.md
 * §"Module taxonomy"), so this is where they are stamped — a call site
 * never has to remember. A call site that names `module` or `route_name`
 * gets no fill-in at all (both always come from one source): `$pageview`
 * resolves the router's pathname, `mutation_completed` carries the
 * submit-time stamp so an offline replay keeps the screen.
 */
export type { CaptureProperties };

export const capture = (
  event: string,
  properties?: CaptureProperties
): void => {
  if (!isPostHogInitialised()) return;
  try {
    // Nothing is resolved when the caller named either property (see above).
    const namesScreen =
      properties?.module !== undefined || 'route_name' in (properties ?? {});
    enqueueAnalyticsEvent(event, {
      ...(namesScreen ? {} : currentScreenProperties()),
      ...properties,
    });
  } catch (error) {
    // A capture failure must never break the caller (routing, the Apollo
    // link chain, the error boundary, ...).
    console.error('Failed to capture product analytics event', event, error);
  }
};
